// app/components/common/Button/ButtonIcon.tsx
'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Icon } from '../Icon';
import { theme } from '../../theme';
import { cn } from '@workspace/lib';

const buttonIconVariants = cva(
  'inline-flex items-center justify-center shrink-0 transition-all duration-200 cursor-pointer outline-none disabled:text-secondary-200 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        round: 'rounded-full bg-white text-primary hover:text-primary-500',
        ghost: 'rounded-md bg-white text-secondary-400 hover:bg-zinc-100 border border-transparent',
        link: 'rounded-none bg-white text-primary hover:underline px-0 py-0 border-0',
        icon: 'rounded-full bg-transparent text-primary hover:text-primary-500 px-0 py-0 border-0',
      },
      size: {
        sm: 'h-8 w-8',
        md: 'h-10 w-10',
        lg: 'h-12 w-12',
      },
      tone: {
        default: '',
        primary: 'text-primary hover:bg-rose-50',
        inverse: 'bg-secondary-400 text-white hover:bg-black/80 border-transparent',
      },
      site: {
        porta: '',
        portaNZ: '',
        redRec: '',
        easycraft: '',
      },
    },
    compoundVariants: [
      {
        tone: 'primary',
        site: 'redRec',
        class: 'hover:bg-primary-100',
      },
    ],
    defaultVariants: { variant: 'round', size: 'md', tone: 'default', site: 'porta' },
  },
);

type ButtonIconV = VariantProps<typeof buttonIconVariants>;

export interface ButtonIconProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  iconName: React.ComponentProps<typeof Icon>['name'];
  iconSize?: number;
  isDisabled?: boolean;
  variant?: ButtonIconV['variant'];
  size?: ButtonIconV['size'];
  tone?: ButtonIconV['tone'];
  ariaLabel?: string;
}

export const ButtonIcon = React.forwardRef<HTMLButtonElement, ButtonIconProps>(
  (
    {
      className,
      iconName,
      iconSize = 20,
      isDisabled,
      variant = 'round',
      size = 'md',
      tone = 'default',
      ariaLabel,
      ...props
    },
    ref,
  ) => {
    const { 'aria-label': ariaLabelProp, ...buttonProps } = props as React.ButtonHTMLAttributes<HTMLButtonElement>;
    const computedAriaLabel = ariaLabel ?? ariaLabelProp ?? iconName;

    return (
      <button
        {...buttonProps}
        ref={ref}
        type="button"
        aria-label={computedAriaLabel}
        aria-disabled={isDisabled || undefined}
        disabled={isDisabled}
        className={cn(buttonIconVariants({ variant, size, tone, site: theme }), className)}
      >
        <Icon name={iconName} size={iconSize} />
      </button>
    );
  },
);
ButtonIcon.displayName = 'ButtonIcon';
